import React from 'react';
import Formsy from 'formsy-react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { withRouter, Redirect } from 'react-router-dom';

import { withStyles } from '@material-ui/core/styles';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';

import FormsyTextField from './FormsyTextField';
import { withApi } from '../components/primitives/Context/ApiContext.tsx';
import ApiManager from '../Api/ApiManager.ts';
import encrypter from './encrypter.ts';

const styles = theme => ({
  card: {
    width: 320,
  },

  cardContent: {
    '& input': {
      margin: '4px 0px',
    },
  },
  media: {
    backgroundImage: 'linear-gradient(141deg, rgba(225, 233, 235, 0.16), rgba(201, 106, 124, 0.08) 75%)',
    padding: theme.spacing(1) * 2,
  },
});

class SignupForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = (this.getInitialState());
    this.signupForm = null;
  }

  getInitialState = () => {
    return {
      hasSubmitted: false,
      canSubmit: true,
      errorText: '',
      errorTitle: '',
      errorDialog: false,
      redirectToReferrer: false,
    };
  };

  handleChange = (e) => {
    this.setState({
      [e.target.name]: e.target.value,
    });
  };

  /**
   * Submits the Signup Form to the database
   *
   * @method submitForm
   * @param {Object} data - The Signup Form data
   */
  submitForm = (data) => {
    const { encrypt } = this.props
    if (data.username === '' || data.password === '' || data.email === '') {
      return;
    }

    const password = encrypt ? encrypter(data.password) : data.password
  
    this.setState({
      hasSubmitted: true,
      canSubmit: false,
    });
  
    const { apiManager } = this.props;
    apiManager.signup(data.username, data.email, data.first_name, data.last_name, password, null, undefined, undefined, encrypt)
      .then(() => {
        if (apiManager.isLoggedIn()) {
          this.setState({
            redirectToReferrer: true,
          });
        } else {
          // If signup failed, reset the form state so that the user can try again.
          this.setState({
            canSubmit: true,
            hasSubmitted: false,
          });
        }
      })
      .catch((error) => {
        console.error(error);
        // If there was an error in the signup process, reset the form state so that the user can try again.
        this.setState({
          canSubmit: true,
          hasSubmitted: false,
        });
      });
  };
  
  

  /**
   * Notifies that signup error has occurred.
   *
   * @method notifySignupError
   * @param {Error} error - The error
   */
  notifySignupError = (error) => {
    this.signupForm.reset();
    this.setState({
      errorDialog: true,
      errorTitle: `Signup Error: ${error.code}`,
      errorText: error.message,
    });
  };

  /**
   * Resets the Signup Form.
   *
   * @method resetSignupForm
   */
  resetSignupForm = () => {
    this.setState(this.getInitialState());
  };

  disableButton = () => {
    this.setState({ canSubmit: false });
  };

  enableButton = () => {
    this.setState({ canSubmit: true });
  };

  /**
   * Renders a Signup Form.
   *
   * @method render
   */
  render() {
    const { from } = this.props.location.state || { from: { pathname: '/' } };
    const { redirectToReferrer,
      canSubmit, hasSubmitted, errorDialog, errorTitle, errorText
    } = this.state;

    if (redirectToReferrer) {
      return <Redirect to={from} />;
    }

    const { classes, intl } = this.props;
    return (
      <React.Fragment>
        <Formsy
          ref={(event) => { this.signupForm = event; }}
          onValid={this.enableButton}
          onInvalid={this.disableButton}
          onValidSubmit={this.submitForm}
        >
          <CardContent className={classes.cardContent}>
            <FormsyTextField
              name="first_name"
              validations="isExisty"
              validationError="This is not a valid first name"
              label={intl.formatMessage({ id: 'firstName' })}
              type="text"
              required
            />
            <FormsyTextField
              name="last_name"
              validations="isExisty"
              validationError="This is not a valid last name"
              label={intl.formatMessage({ id: 'lastName' })}
              type="text"
              required
            />
            <FormsyTextField
              name="email"
              validations="isEmail"
              validationError="This is not a valid email"
              label={intl.formatMessage({ id: 'email' })}
              type="text"
              required
            />
            <FormsyTextField
              name="username"
              validations="isExisty"
              validationError={intl.formatMessage({ id: 'validations.username' })}
              label={intl.formatMessage({ id: 'username' })}
              type="text"
              required
            />
            <FormsyTextField
              name="password"
              validations="isExisty"
              validationError={intl.formatMessage({ id: 'validations.password' })}
              label={intl.formatMessage({ id: 'password' })}
              type="password"
              required
            />
            <FormsyTextField
              name="confirm_password"
              validations={{
                passwordValidation: function (values, value) {
                  return (values.password) === (value);
                }
              }}
              validationError={intl.formatMessage({ id: 'validations.password' })}
              label={intl.formatMessage({ id: 'confirmPassword' })}
              type="password"
              required
            />
          </CardContent>
          <CardActions>
            <Button
              fullWidth
              type="submit"
              color="secondary"
              variant="contained"
              disabled={!canSubmit || hasSubmitted}
            >
              {intl.formatMessage({ id: 'signup' })}
            </Button>
          </CardActions>
        </Formsy>
        <Dialog
          fullWidth
          open={errorDialog}
          onClose={this.resetSignupForm}
        >
          <DialogTitle>{errorTitle}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {errorText}
            </DialogContentText>
          </DialogContent>
        </Dialog>
      </React.Fragment>
    );
  }
}

SignupForm.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  location: PropTypes.instanceOf(Object).isRequired,
  intl: PropTypes.instanceOf(Object).isRequired,
  apiManager: PropTypes.instanceOf(ApiManager).isRequired,
};

export default withStyles(styles)(injectIntl(withRouter(withApi(SignupForm))));
